// src/services/mailingListStore.js
// Communication layer between UI and mailing server

import axios from 'axios';

// API URL (configurable for development vs production)
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/mailapi/mail/mailing-list'  // In production, use relative path to same server
  : 'http://localhost:3003/mailapi/mail/mailing-list'; // In development, use localhost

class MailingListStore {
  constructor() {
    this.subscribers = [];
    this.stats = {};
    this.isLoading = false;
    this.error = null;
  }
  
  // Subscribe a new email
  async subscribeEmail(email, name = '', source = 'website') {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.post(`${API_URL}/subscribe`, {
        email,
        name,
        source,
        timestamp: new Date().toISOString()
      });
      
      this.isLoading = false;
      return {
        success: true,
        message: response.data.message || 'Successfully subscribed to the mailing list',
        id: response.data.id
      };
    } catch (error) {
      this.isLoading = false;
      
      // Check for already subscribed (409 status)
      if (error.response && error.response.status === 409) {
        return {
          success: false,
          exists: true,
          message: error.response.data.message || 'This email is already subscribed'
        };
      }
      
      this.error = error.response?.data?.message || error.message || 'Error subscribing to mailing list';
      return {
        success: false,
        message: this.error
      };
    }
  }
  
  // Get all subscribers (admin only)
  async getSubscribers() {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.get(`${API_URL}/subscribers`);
      this.subscribers = response.data;
      this.isLoading = false;
      return this.subscribers;
    } catch (error) {
      this.isLoading = false;
      this.error = error.response?.data?.message || error.message || 'Error fetching subscribers';
      return [];
    }
  }
  
  // Get subscriber stats (admin only)
  async getStats() {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.get(`${API_URL}/stats`);
      this.stats = response.data;
      this.isLoading = false;
      return this.stats;
    } catch (error) {
      this.isLoading = false;
      this.error = error.response?.data?.message || error.message || 'Error fetching stats';
      return {};
    }
  }
  
  // Send confirmation email to a subscriber (admin only)
  async sendConfirmation(email, firstName = '') {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.post(`${API_URL}/send-confirmation/${email}`, { firstName });
      this.isLoading = false;
      return {
        success: true,
        message: response.data.message || `Confirmation sent to ${email}`,
        messageId: response.data.messageId
      };
    } catch (error) {
      this.isLoading = false;
      this.error = error.response?.data?.message || error.message || 'Error sending confirmation';
      return {
        success: false,
        message: this.error
      };
    }
  }
  
  // Send newsletter to all subscribers (admin only)
  async sendNewsletter(settings = {}) {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.post(`${API_URL}/send-newsletter`, settings);
      this.isLoading = false;
      return {
        success: true,
        message: response.data.message || 'Newsletter sent successfully',
        campaignId: response.data.campaignId
      };
    } catch (error) {
      this.isLoading = false;
      this.error = error.response?.data?.message || error.message || 'Error sending newsletter';
      return {
        success: false,
        message: this.error
      };
    }
  }
  
  // Toggle subscriber status (admin only)
  async updateSubscriberStatus(id, subscribed) {
    this.isLoading = true;
    this.error = null;
    
    try {
      const response = await axios.put(`${API_URL}/subscribers/${id}/status`, { subscribed });
      this.isLoading = false;
      return {
        success: true,
        message: response.data.message || `Subscriber ${subscribed ? 'resubscribed' : 'unsubscribed'} successfully`,
        subscriber: response.data.subscriber
      };
    } catch (error) {
      this.isLoading = false;
      this.error = error.response?.data?.message || error.message || 'Error updating subscriber status';
      return {
        success: false,
        message: this.error
      };
    }
  }
}

// Create and export a singleton instance
const mailingListStore = new MailingListStore();
export default mailingListStore;